/**
 * Combat-Sandbox (SCOPE_STAGE1 §4, seit Stufe 2 als eigener Screen im Hauptmenue):
 * team editor, seed, fight, playback, debug panel.
 * UI code may use floats/Math.random freely — the sim never does.
 */
import { cloneConfig, defaultConfig, defaultLoadout, type BalanceConfig } from '../config/balance'
import { simulate } from '../sim/simulate'
import { presets } from '../sim/presets'
import type { CombatResult, TeamId, UnitSetup } from '../sim/types'
import { Renderer, ROLE_COLORS, type EditView } from '../render/renderer'
import { buildDebugPanel } from './debugPanel'

export function mountSandbox(app: HTMLElement, workingConfig: BalanceConfig, onBack: () => void): () => void {
  app.innerHTML = `
  <header>
    <button id="sbBack" title="Zurueck zum Hauptmenue">☰ Menü</button>
    <h1>⚔️ Autobattler <span>Combat-Sandbox</span></h1>
    <div class="row">
      <label>Seed</label>
      <input type="number" id="seed" value="42" />
      <button id="randSeed" title="Neuer zufälliger Seed">🎲</button>
      <button id="fight" class="primary">Kampf starten</button>
    </div>
    <div class="row">
      <button id="presetMirror" title="1× jeder Champion pro Seite">Mirror</button>
      <button id="presetWall" title="3× Sir Kohlrab + 2× Bruder Knobi vs 3× Auberon + 2× Möhrax">Wall vs Burst</button>
      <button id="presetDot" title="4× Okrazius + 1× Bruder Knobi vs Mirror-Team">DoT-Stack</button>
      <button id="clearTeams">Leeren</button>
    </div>
  </header>
  <div class="main">
    <div class="stage">
      <div id="overtimeBadge" class="badge hidden">OVERTIME ×2</div>
      <canvas id="battlefield"></canvas>
      <div id="resultOverlay" class="overlay hidden"></div>
      <div class="tickbar" id="tickbar">
        <button id="playPause">⏸</button>
        <button id="speed1" class="active">1×</button>
        <button id="speed2">2×</button>
        <button id="speed4">4×</button>
        <button id="restart" title="Neustart mit gleichem Seed — beweist Determinismus">⟲ Restart</button>
        <button id="backToEdit">✎ Editor</button>
        <input type="range" id="scrubber" min="0" max="100" value="0" step="1" />
        <span class="time" id="timeLabel">Tick 0</span>
      </div>
    </div>
    <div class="sidebar">
      <div class="card" id="editorCard">
        <h3>Team-Editor</h3>
        <div class="hint" style="margin-bottom:8px">
          Champion wählen, dann aufs Grid klicken.<br>
          Obere Hälfte = <b style="color:var(--teamB)">Team B</b>, untere = <b style="color:var(--teamA)">Team A</b>.<br>
          Rechtsklick entfernt eine Unit.
        </div>
        <div class="roster" id="roster"></div>
        <div id="unitPanel" class="hidden" style="margin-top:10px">
          <h3 id="unitPanelTitle">Unit</h3>
          <div class="row"><label>Hand 1</label><select id="weapon0"></select></div>
          <div class="row"><label>Hand 2</label><select id="weapon1"></select></div>
          <div class="row"><button id="removeUnit">Entfernen</button></div>
        </div>
        <div class="row" style="margin-top:10px">
          <span class="hint" id="teamCount">A: 0 · B: 0</span>
        </div>
      </div>
      <div class="card" id="resultCard" style="display:none">
        <h3>Ergebnis</h3>
        <div id="resultText" class="hint"></div>
      </div>
      <div class="card">
        <h3>Debug-Panel — Balance-Config</h3>
        <div id="debugPanel"></div>
      </div>
      <div class="card">
        <h3>Legende</h3>
        <div class="legend" id="legend"></div>
      </div>
    </div>
  </div>
`

// ---------- state ----------

interface AppState {
  mode: 'edit' | 'battle'
  teamA: UnitSetup[]
  teamB: UnitSetup[]
  brush: string | null // champion key
  hover: { col: number; row: number } | null
  selected: { team: TeamId; index: number } | null
  config: BalanceConfig // working copy edited by debug panel
  result: CombatResult | null
  playing: boolean
  speed: number
  simTime: number // fractional ticks
}

const state: AppState = {
  mode: 'edit',
  teamA: [],
  teamB: [],
  brush: 'ironguard',
  hover: null,
  selected: null,
  config: workingConfig,
  result: null,
  playing: false,
  speed: 1,
  simTime: 0,
}

const canvas = document.getElementById('battlefield') as HTMLCanvasElement
const renderer = new Renderer(canvas, { cols: state.config.grid.cols, rows: state.config.grid.rowsPerSide * 2 })
renderer.attachCameraControls(canvas) // Rad = Zoom, Mitteltaste/Space+Drag = Pan, 0 = Reset (BATTLE-17)

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T

// ---------- roster ----------

function buildRoster(): void {
  const roster = $('#roster'.slice(1))
  roster.innerHTML = ''
  for (const [key, champ] of Object.entries(state.config.champions)) {
    const btn = document.createElement('button')
    btn.dataset.champ = key
    const dot = document.createElement('span')
    dot.className = 'dot'
    dot.style.background = ROLE_COLORS[champ.role]
    btn.appendChild(dot)
    const label = document.createElement('span')
    label.textContent = champ.name
    btn.appendChild(label)
    if (state.brush === key) btn.classList.add('active')
    btn.addEventListener('click', () => {
      state.brush = state.brush === key ? null : key
      state.selected = null
      buildRoster()
      updateUnitPanel()
    })
    roster.appendChild(btn)
  }
}

function buildLegend(): void {
  const legend = $('legend')
  legend.innerHTML = ''
  const entries: Array<[string, string]> = [
    ...Object.entries(ROLE_COLORS).map(([role, color]) => [role, color] as [string, string]),
  ]
  for (const [name, color] of entries) {
    const span = document.createElement('span')
    const dot = document.createElement('span')
    dot.className = 'dot'
    dot.style.width = '10px'
    dot.style.height = '10px'
    dot.style.background = color
    span.appendChild(dot)
    span.appendChild(document.createTextNode(name))
    legend.appendChild(span)
  }
  const extra = document.createElement('span')
  extra.textContent = '🟢 Gift · 🟠 Brennen · ⚑ Taunt'
  legend.appendChild(extra)
}

// ---------- team editing ----------

function teamOfRow(row: number): TeamId {
  return row < state.config.grid.rowsPerSide ? 'B' : 'A'
}

function unitAt(col: number, row: number): { team: TeamId; index: number } | null {
  const a = state.teamA.findIndex((u) => u.hex.col === col && u.hex.row === row)
  if (a >= 0) return { team: 'A', index: a }
  const b = state.teamB.findIndex((u) => u.hex.col === col && u.hex.row === row)
  if (b >= 0) return { team: 'B', index: b }
  return null
}

function selectedUnit(): UnitSetup | null {
  if (!state.selected) return null
  const arr = state.selected.team === 'A' ? state.teamA : state.teamB
  return arr[state.selected.index] ?? null
}

function updateTeamCount(): void {
  $('teamCount').textContent = `A: ${state.teamA.length} · B: ${state.teamB.length}`
  ;($('fight') as HTMLButtonElement).disabled = state.teamA.length === 0 || state.teamB.length === 0
}

function handsOf(weapons: string[]): number {
  let h = 0
  for (const w of weapons) h += state.config.weapons[w]?.hands ?? 1
  return h
}

function updateUnitPanel(): void {
  const panel = $('unitPanel')
  const u = selectedUnit()
  if (!u) {
    panel.classList.add('hidden')
    return
  }
  panel.classList.remove('hidden')
  const champ = state.config.champions[u.champ]!
  $('unitPanelTitle').textContent = `${champ.name} (Team ${state.selected!.team})`

  const weaponKeys = Object.keys(state.config.weapons).filter((k) => k !== 'fist')
  for (const slot of [0, 1] as const) {
    const sel = $(`weapon${slot}`) as HTMLSelectElement
    sel.innerHTML = ''
    const none = document.createElement('option')
    none.value = ''
    none.textContent = '— (Faust)'
    sel.appendChild(none)
    const otherSlot = slot === 0 ? 1 : 0
    const otherKey = u.weapons[otherSlot]
    const otherHands = otherKey ? state.config.weapons[otherKey]?.hands ?? 1 : 0
    for (const k of weaponKeys) {
      const w = state.config.weapons[k]!
      if (w.hands + otherHands > 2) continue // hand slots: max 2 (docs/05)
      const opt = document.createElement('option')
      opt.value = k
      opt.textContent = `${w.name} (${w.hands}H, ${w.base} dmg, R${w.range})`
      sel.appendChild(opt)
    }
    sel.value = u.weapons[slot] ?? ''
    sel.onchange = () => {
      const next = [...u.weapons]
      if (sel.value === '') {
        next.splice(slot, 1)
      } else {
        next[slot] = sel.value
      }
      const cleaned = next.filter((x): x is string => Boolean(x))
      if (handsOf(cleaned) > 2) {
        // two-hander picked -> drop the other weapon
        u.weapons = [sel.value]
      } else {
        u.weapons = cleaned
      }
      updateUnitPanel()
    }
  }
}

function loadPreset(name: keyof typeof presets): void {
  const p = presets[name]!()
  state.teamA = p.a.map((u) => ({ ...u, hex: { ...u.hex }, weapons: [...u.weapons] }))
  state.teamB = p.b.map((u) => ({ ...u, hex: { ...u.hex }, weapons: [...u.weapons] }))
  state.selected = null
  updateTeamCount()
  updateUnitPanel()
}

// ---------- canvas input ----------

function canvasPos(ev: MouseEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect()
  return { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
}

canvas.addEventListener('mousemove', (ev) => {
  if (state.mode !== 'edit') return
  const { x, y } = canvasPos(ev)
  state.hover = renderer.hexAt(x, y)
})

canvas.addEventListener('mouseleave', () => {
  state.hover = null
})

canvas.addEventListener('click', (ev) => {
  if (state.mode !== 'edit') return
  const { x, y } = canvasPos(ev)
  const hex = renderer.hexAt(x, y)
  if (!hex) return
  const existing = unitAt(hex.col, hex.row)
  if (existing) {
    state.selected = existing
    updateUnitPanel()
    return
  }
  if (!state.brush) return
  const team = teamOfRow(hex.row)
  const setup: UnitSetup = {
    champ: state.brush,
    hex: { ...hex },
    weapons: [...(defaultLoadout[state.brush] ?? [])],
  }
  if (team === 'A') state.teamA.push(setup)
  else state.teamB.push(setup)
  state.selected = { team, index: (team === 'A' ? state.teamA : state.teamB).length - 1 }
  updateTeamCount()
  updateUnitPanel()
})

canvas.addEventListener('contextmenu', (ev) => {
  if (state.mode !== 'edit') return
  ev.preventDefault()
  const { x, y } = canvasPos(ev)
  const hex = renderer.hexAt(x, y)
  if (!hex) return
  const existing = unitAt(hex.col, hex.row)
  if (!existing) return
  const arr = existing.team === 'A' ? state.teamA : state.teamB
  arr.splice(existing.index, 1)
  state.selected = null
  updateTeamCount()
  updateUnitPanel()
})

// ---------- fight / playback ----------

function currentSeed(): number {
  const v = Number(($('seed') as HTMLInputElement).value)
  return Number.isFinite(v) ? Math.abs(Math.round(v)) >>> 0 : 42
}

function startFight(): void {
  if (state.teamA.length === 0 || state.teamB.length === 0) return
  const cfg = cloneConfig(state.config) // config snapshot: debug changes apply to NEXT fight
  let result: CombatResult
  try {
    result = simulate(currentSeed(), state.teamA, state.teamB, cfg)
  } catch (err) {
    alert(`Simulation fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    return
  }
  state.result = result
  renderer.setDims({ cols: cfg.grid.cols, rows: cfg.grid.rowsPerSide * 2 })
  renderer.prepare(result)
  state.mode = 'battle'
  state.simTime = 0
  state.playing = true
  state.selected = null
  state.hover = null
  const scrubber = $('scrubber') as HTMLInputElement
  scrubber.max = String(result.endTick)
  scrubber.value = '0'
  $('resultOverlay').classList.add('hidden')
  $('resultCard').style.display = 'none'
  updateModeUi()
}

function showResult(): void {
  const r = state.result
  if (!r) return
  const winnerLabel = r.winner === 'draw' ? 'Unentschieden' : `Team ${r.winner} gewinnt`
  const color = r.winner === 'A' ? 'var(--teamA)' : r.winner === 'B' ? 'var(--teamB)' : 'var(--muted)'
  const reasonLabel = { elimination: 'Elimination', cutoff: `Hard Cutoff (Tick ${r.endTick}) — Wertung`, mutual: 'Beide Seiten eliminiert' }[r.reason]
  const pct = (bp: number): string => `${(bp / 100).toFixed(1)}%`
  const html = `
    <h2 style="color:${color}">${winnerLabel}</h2>
    <table>
      <tr><td>Grund</td><td>${reasonLabel}</td></tr>
      <tr><td>Dauer</td><td>${r.endTick} Ticks (${(r.endTick / state.config.combat.tickRate).toFixed(1)}s)</td></tr>
      <tr><td>Power A</td><td style="color:var(--teamA)">${pct(r.scoreA.powerBp)} · ${r.scoreA.unitsLeft}/${r.scoreA.unitsStart} Units</td></tr>
      <tr><td>Power B</td><td style="color:var(--teamB)">${pct(r.scoreB.powerBp)} · ${r.scoreB.unitsLeft}/${r.scoreB.unitsStart} Units</td></tr>
      <tr><td>Formel</td><td class="hint">0.6×Units + 0.4×HP (Config)</td></tr>
    </table>
    <div class="row" style="justify-content:center">
      <button id="overlayRestart">⟲ Gleicher Seed</button>
      <button id="overlayNewSeed">🎲 Neuer Seed</button>
      <button id="overlayEdit" class="primary">✎ Editor</button>
    </div>
  `
  const overlay = $('resultOverlay')
  overlay.innerHTML = `<div class="box">${html}</div>`
  overlay.classList.remove('hidden')
  document.getElementById('overlayRestart')!.addEventListener('click', () => startFight())
  document.getElementById('overlayNewSeed')!.addEventListener('click', () => {
    rollSeed()
    startFight()
  })
  document.getElementById('overlayEdit')!.addEventListener('click', backToEdit)

  $('resultCard').style.display = ''
  $('resultText').innerHTML =
    `<b style="color:${color}">${winnerLabel}</b> — ${reasonLabel}<br>` +
    `Dauer ${r.endTick} Ticks · Power A ${pct(r.scoreA.powerBp)} / B ${pct(r.scoreB.powerBp)}`
}

function backToEdit(): void {
  state.mode = 'edit'
  state.playing = false
  $('resultOverlay').classList.add('hidden')
  updateModeUi()
}

function updateModeUi(): void {
  const battle = state.mode === 'battle'
  $('tickbar').style.visibility = battle ? 'visible' : 'hidden'
  $('editorCard').style.opacity = battle ? '0.45' : '1'
  ;($('playPause') as HTMLButtonElement).textContent = state.playing ? '⏸' : '▶'
  for (const s of [1, 2, 4]) {
    $(`speed${s}`).classList.toggle('active', state.speed === s)
  }
}

function rollSeed(): void {
  ;($('seed') as HTMLInputElement).value = String(Math.floor(Math.random() * 0xffffffff) >>> 0)
}

$('fight').addEventListener('click', startFight)
$('randSeed').addEventListener('click', rollSeed)
$('presetMirror').addEventListener('click', () => loadPreset('mirror'))
$('presetWall').addEventListener('click', () => loadPreset('wallVsBurst'))
$('presetDot').addEventListener('click', () => loadPreset('dotStack'))
$('clearTeams').addEventListener('click', () => {
  state.teamA = []
  state.teamB = []
  state.selected = null
  updateTeamCount()
  updateUnitPanel()
})
$('playPause').addEventListener('click', () => {
  state.playing = !state.playing
  updateModeUi()
})
for (const s of [1, 2, 4]) {
  $(`speed${s}`).addEventListener('click', () => {
    state.speed = s
    updateModeUi()
  })
}
$('restart').addEventListener('click', () => startFight())
$('backToEdit').addEventListener('click', backToEdit)
;($('scrubber') as HTMLInputElement).addEventListener('input', () => {
  if (!state.result) return
  state.simTime = Number(($('scrubber') as HTMLInputElement).value)
  state.playing = false
  $('resultOverlay').classList.add('hidden')
  updateModeUi()
})

const onResize = (): void => renderer.resize()
window.addEventListener('resize', onResize)

// ---------- render loop ----------

let running = true
let lastFrame = performance.now()

function frame(now: number): void {
  if (!running) return
  const dt = Math.min(0.1, (now - lastFrame) / 1000)
  lastFrame = now

  if (state.mode === 'edit') {
    const view: EditView = {
      unitsA: state.teamA.map((u) => ({
        col: u.hex.col, row: u.hex.row,
        champ: u.champ,
        role: state.config.champions[u.champ]!.role,
        weapons: u.weapons,
      })),
      unitsB: state.teamB.map((u) => ({
        col: u.hex.col, row: u.hex.row,
        champ: u.champ,
        role: state.config.champions[u.champ]!.role,
        weapons: u.weapons,
      })),
      hover: state.hover,
      selected: selectedUnit()?.hex ?? null,
      brushRole: state.brush ? state.config.champions[state.brush]!.role : null,
    }
    renderer.drawEdit(view)
  } else if (state.result) {
    const r = state.result
    if (state.playing) {
      state.simTime += dt * state.config.combat.tickRate * state.speed
      if (state.simTime >= r.endTick) {
        state.simTime = r.endTick
        state.playing = false
        updateModeUi()
        showResult()
      }
      ;($('scrubber') as HTMLInputElement).value = String(Math.floor(state.simTime))
    }
    renderer.drawBattle(state.simTime, now)
    const tick = Math.floor(state.simTime)
    const secs = (tick / state.config.combat.tickRate).toFixed(1)
    $('timeLabel').textContent = `Tick ${tick} / ${r.endTick} · ${secs}s`
    const cfgOt = state.config.combat.overtimeStartTick
    $('overtimeBadge').classList.toggle('hidden', tick < cfgOt)
  }
  requestAnimationFrame(frame)
}

// ---------- boot ----------

$('sbBack').addEventListener('click', onBack)
buildRoster()
buildLegend()
buildDebugPanel($('debugPanel'), state.config, defaultConfig)
loadPreset('mirror')
updateModeUi()
updateTeamCount()
requestAnimationFrame(frame)

return () => {
  running = false
  window.removeEventListener('resize', onResize)
}
}
